/* eslint-disable max-len */
/* eslint-disable no-use-before-define */
/* eslint-disable no-plusplus */
import {
  faFastBackward,
  faFastForward,
  faStepBackward,
  faStepForward,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import usePaging from 'hooks/usePaging';
import React, { Fragment, ReactElement, useState } from 'react';
import PageSizeSelector from './PageSizeSelector';

interface Props<T> {
  /**
   * The items to page
   */
  items: T[];
  /**
   * Function to render items on page
   */
  renderItem: (item: T, index: number) => ReactElement;
  /**
   * Whether or not a page size selector should be shown
   */
  sizeSelector?: boolean;
  defaultPageSize?: number;
  showPages?: number;
  hideNextButtons?: boolean;
  [x: string]: any;
}

const PassivePagination = <T, >({
  items,
  renderItem,
  sizeSelector,
  defaultPageSize,
  showPages,
  hideNextButtons,
  ...rest
}: Props<T>): ReactElement => {
  const [pageSize, setPageSize] = useState(defaultPageSize || 10);

  const [pageItems, page, total, changePage] = usePaging(items, pageSize);

  // Memorize the real page, eg the page that can be shown.
  // Unexpected side effect is that changing page size does not move the state away from the page
  // and therefore returns to the previous page if user changes page size back

  return (
    <>
      {pageItems.map((item, index) => renderItem(item, page * pageSize + index))}
      <tr className="pager-row" {...rest}>
        <td colSpan={100}>
          <div className="pager-shifter">
            <RenderPager
              showPages={showPages}
              totalPages={total}
              currentPage={page}
              onChange={changePage}
              hideNextButtons={hideNextButtons}
            />
          </div>
          {sizeSelector && (
            <div className="float-left pager-control">
              <PageSizeSelector
                value={pageSize}
                onChange={(size: number) => setPageSize(size)}
                rowCount={items.length}
              />
            </div>
          )}
        </td>
      </tr>
    </>
  );
};

export default PassivePagination;

interface PagerProps {
  /**
   * The total number of pages
   */
  totalPages: number;
  /**
   * The current page
   */
  currentPage: number;
  /**
   * Fires when another page is selected, currentPage should be updated as effect of this, otherwise the page will not change
   */
  onChange: (page: number) => void;
  showPages?: number;
  hideNextButtons?: boolean;
}

/**
 * Renders a page selector
 * @param param0
 */
const RenderPager: React.FC<PagerProps> = ({
  currentPage,
  totalPages,
  onChange,
  showPages,
  hideNextButtons,
}) => {
  const pages = [];
  // Get page numbers, 3 behind and 3 in front of current
  for (let index = 0; index < totalPages; index++) {
    if (index === currentPage) {
      pages.push(index);
    } else if (
      index > currentPage - (showPages || 3)
      && index < currentPage + (showPages || 3)
    ) {
      pages.push(index);
    }
  }
  return (
    <nav aria-label="Side skifter">
      <ul className="pagination">
        {/* first page control should be drawn when at page 1 or greater */}
        {currentPage > 1 && (
          <li className="page-item">
            <button
              type="button"
              className="page-link"
              title="Første"
              onClick={() => onChange(0)}
            >
              <div className="page-back-icon">
                <FontAwesomeIcon icon={faFastBackward} />
              </div>
              <span>1</span>
            </button>
          </li>
        )}
        {/* prev page control should be drawn when at page 0 or greater */}
        {currentPage > 0 && !hideNextButtons && (
          <li className="page-item">
            <button
              type="button"
              className="page-link"
              title="Forrige"
              onClick={() => onChange(currentPage - 1)}
            >
              <FontAwesomeIcon icon={faStepBackward} />
            </button>
          </li>
        )}

        {pages.map((page) => (
          <li
            key={page}
            className={page === currentPage ? 'page-item active' : 'page-item'}
          >
            <button
              type="button"
              className="page-link"
              key={page}
              onClick={() => onChange(page)}
            >
              {page + 1}
              {page === currentPage && (
                <span className="sr-only">(nuværende)</span>
              )}
            </button>
          </li>
        ))}

        {/* next page control should be drawn when at lastpage -1 or less */}
        {currentPage < totalPages - 1 && !hideNextButtons && (
          <li className="page-item">
            <button
              type="button"
              className="page-link"
              title="Næste"
              onClick={() => onChange(currentPage + 1)}
            >
              <FontAwesomeIcon icon={faStepForward} />
            </button>
          </li>
        )}
        {/* last page control should be drawn when at lastpage -2 or less */}
        {currentPage < totalPages - 2 && (
          <li className="page-item">
            <button
              type="button"
              className="page-link"
              title="Sidste"
              onClick={() => onChange(totalPages - 1)}
            >
              <span>{totalPages}</span>
              <div className="page-next-icon">
                <FontAwesomeIcon icon={faFastForward} />
              </div>
            </button>
          </li>
        )}
      </ul>
    </nav>
  );
};
